import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSignupDataGtkmData } from '../store/signup-data/selectors';
import { Data } from 'signup-constants/signupData/Data';
import { getGTKMDataNotEmptyCount } from '../http-gtkm-company-info/updateStepOrders';
import { CopyKey } from '../constants/gtkmDomainHelperText';
import { useTimer } from './useTimer';
import { getGtkmAnimationState, getGtkmHasDomainChangedInDomainStep } from '../store/gtkm/selector';
import { setGtkmShowingErrorModal, setGtkmAnimationStop, setGtkmAnimationNotDisplayed } from '../store/gtkm/action-creator';
import { GtkmAnimationState } from '../store/gtkm/types';
import { isEndpointStatusError, isEndpointStatusSuccess } from '../store/http-responses/selectors';
import { useView } from '../views/useView';
import { Step } from 'signup-constants/Step';
import { getEndpointTimings } from '../store/http-responses/selectors';
import { getEndpointRetries } from '../store/http-responses/selectors';
import { gtkmDomainHelperTextConfig } from '../constants/gtkmDomainHelperText';
import { getNextStep } from '../store/flow/selectors';
import { getGTKMEndpointKey } from '../utils/gtkm/getGTKMEndpointKey';
export const ERROR_THRESHOLD = 8;
export const MIN_TIME_FOR_ANIMATION = 1.5;
export const MIN_TIME_FOR_LOADING_AND_ANALYZING = MIN_TIME_FOR_ANIMATION * 2;
export const MIN_TIME_TOTAL_FOR_NO_ERROR = MIN_TIME_FOR_ANIMATION * 3;
export const useSuccessGTKMDomainHelperTextKey = () => {
  const gtkmIndustry = useSelector(state => getSignupDataGtkmData(state, Data.Industry));
  const gtkmCompanySize = useSelector(state => getSignupDataGtkmData(state, Data.CompanySize));
  const gtkmCompanyName = useSelector(state => getSignupDataGtkmData(state, Data.CompanyName));
  const gtkmDomain = useSelector(state => getSignupDataGtkmData(state, Data.CompanyDomain));
  const gtkmDataNotEmptyCount = getGTKMDataNotEmptyCount({
    [Data.Industry]: gtkmIndustry,
    [Data.CompanySize]: gtkmCompanySize,
    [Data.CompanyName]: gtkmCompanyName,
    [Data.CompanyDomain]: gtkmDomain
  });
  if (gtkmDataNotEmptyCount === 4) {
    return CopyKey.FoundAllInfo;
  }
  if (gtkmDataNotEmptyCount === 3) {
    return CopyKey.FoundTwoInfo;
  }
  if (gtkmDataNotEmptyCount === 2) {
    return CopyKey.FoundOneInfo;
  }
  return CopyKey.NoInfo;
};
export const useGTKMDomainHelperTextKey = () => {
  const {
    elapsedTime,
    start: startTimer,
    stop: stopTimer,
    currentTime
  } = useTimer();
  const dispatch = useDispatch();
  const gtkmEndpointKey = useSelector(getGTKMEndpointKey);
  const animationState = useSelector(state => getGtkmAnimationState(state));
  const timings = useSelector(state => getEndpointTimings(state, gtkmEndpointKey));
  const endpointError = useSelector(isEndpointStatusError(gtkmEndpointKey));
  const endpointSuccess = useSelector(isEndpointStatusSuccess(gtkmEndpointKey));
  const retryCount = useSelector(state => getEndpointRetries(state, gtkmEndpointKey));
  const nextStep = useSelector(getNextStep);
  const hasFinishedUpdateStepOrders = nextStep !== Step.LoadingPage;
  const successGTKMDomainHelperTextKey = useSuccessGTKMDomainHelperTextKey();
  const {
    submit
  } = useView({
    stepKey: Step.CompanyDomain
  });
  const hasDomainChanged = useSelector(state => getGtkmHasDomainChangedInDomainStep(state));
  useEffect(() => {
    return () => {
      dispatch(setGtkmAnimationNotDisplayed());
    };
  }, [dispatch]);
  useEffect(() => {
    if (animationState === GtkmAnimationState.Started) {
      startTimer();
    }
  }, [animationState, startTimer]);
  useEffect(() => {
    if (animationState === GtkmAnimationState.Stopped) {
      stopTimer();
      submit();
    }
  }, [animationState, stopTimer, submit, dispatch]);
  useEffect(() => {
    // before showing error modal, show loading and analyzing text for at least 1.5 seconds each
    if (retryCount === 1 && endpointError && hasFinishedUpdateStepOrders && elapsedTime >= MIN_TIME_FOR_LOADING_AND_ANALYZING) {
      dispatch(setGtkmAnimationStop());
      dispatch(setGtkmShowingErrorModal(true));
    }
  }, [endpointError, elapsedTime, dispatch, retryCount, hasFinishedUpdateStepOrders]);
  useEffect(() => {
    // show success animation text at least 1.5 seconds after the endpoint is successful
    if (retryCount === 1 && endpointSuccess && hasFinishedUpdateStepOrders && timings !== null && timings !== void 0 && timings.timeCompleted) {
      const timeTaken = (currentTime - timings.timeCompleted.getTime()) / 1000;
      if (timeTaken >= MIN_TIME_FOR_ANIMATION && elapsedTime >= MIN_TIME_TOTAL_FOR_NO_ERROR) {
        dispatch(setGtkmAnimationStop());
      }
    }
  }, [endpointSuccess, currentTime, timings, dispatch, elapsedTime, retryCount, hasFinishedUpdateStepOrders]);
  useEffect(() => {
    // when the domain has not changed, show loading, analyzing and no info text for at least 1.5 seconds each
    if (!hasDomainChanged && hasFinishedUpdateStepOrders && elapsedTime >= MIN_TIME_TOTAL_FOR_NO_ERROR) {
      dispatch(setGtkmAnimationStop());
    }
  }, [hasDomainChanged, dispatch, elapsedTime, hasFinishedUpdateStepOrders]);
  const copyKey = useMemo(() => {
    if (animationState === GtkmAnimationState.Started) {
      // show loading text for at least 1.5 seconds
      if (elapsedTime < MIN_TIME_FOR_ANIMATION) {
        return CopyKey.Loading;
      }

      // show analyzing text for at least 1.5 seconds
      if (elapsedTime < MIN_TIME_FOR_LOADING_AND_ANALYZING) {
        return CopyKey.Analyzing;
      }
      if (retryCount === 1 && endpointSuccess && timings !== null && timings !== void 0 && timings.timeCompleted) {
        return successGTKMDomainHelperTextKey;
      }
      if (!hasDomainChanged) {
        return CopyKey.NoInfo;
      }
      return CopyKey.Analyzing;
    }
    return CopyKey.default;
  }, [animationState, successGTKMDomainHelperTextKey, elapsedTime, endpointSuccess, hasDomainChanged, retryCount, timings === null || timings === void 0 ? void 0 : timings.timeCompleted]);
  const copyMessageKey = useMemo(() => gtkmDomainHelperTextConfig[copyKey], [copyKey]);
  const isGTKMAnimationRunning = useMemo(() => animationState === GtkmAnimationState.Started, [animationState]);
  return {
    copyKey,
    isGTKMAnimationRunning,
    copyMessageKey
  };
};